define((require) => {
    "use strict";

    const {escape}        = require("underscore");
    const EventDispatcher = require("shared/util/EventDispatcher");
    const MathUtil        = require("shared/util/MathUtil").default;
    const NotificationBar = require("editor/ui/notificationbar/NotificationBar");
    const Permissions     = require("shared/security/Permissions");

    const DEFAULT_CLOSE_DELAY = 7000;

    let instance = null;

    /**
     * Global service used to communicate different messages to the user.
     *
     * It will present info-bars to the user when needed and it will interact with the
     * API to handle the confirmation process.
     *
     * @author Andreea Vasile (avi)
     */
    const SystemMessages = EventDispatcher.extend({ // TODO: why is this an event dispatcher?

        /**
         * Creates the purge message used in the {@link notifyPurge} method.
         * @param {String} modelName The name of the model that needs to be purged, eg: Experience or Channel.
         * @param {String} operationName The name of the operation that triggered a purge. May be a save (channelConfig) or a push (Experience).
         * @param {Number} purgeTime The time it takes to purge the requests expressed in seconds; can be null or even negative.
         * @param {Boolean} [scheduledNotification] If set, it will add the scheduled notification warning about delays between the expected live live time and the actual time.
         * @returns {String} The purge message to display to the user.
         */
        createPurgeMessage(modelName, operationName, purgeTime, scheduledNotification) { // TODO: I think this should use a templating scheme with options instead
            const minutesToPush = purgeTime === null ? null : MathUtil.round(purgeTime / 60, 0); // null if nothing pushed, can actually happen!
            let message = `${modelName} successfully ${operationName}`;

            // Nothing purged at all...
            if (purgeTime !== null) {
                message += ". The Content Delivery Network ";

                // Somebody else purged this concurrently and we do therefor not know the time it will take for
                // the purge to complete...
                if (purgeTime < 0) {
                    message += "is expected to be updated shortly";

                // Immediately, or sometime in the future...
                } else {
                    const timeUntilPushes = minutesToPush === 0 ? "immediately" : `within ${minutesToPush} minutes`;
                    message += `will be updated ${timeUntilPushes}`;
                }

                if (scheduledNotification) {
                    message += " from now or from the scheduled go live time";
                }
            }
            return `${message}.`;
        },

        /**
         * Notifies the user that a purge operation is currently pending.
         * @param {String} modelName The name of the model that needs to be purged, eg: Experience or Channel.
         * @param {String} operationName The name of the operation that triggered a purge. May be a save (channelConfig) or a push (Experience).
         * @param {Number} purgeTime The time it takes to purge the requests expressed in seconds.
         * @param {Boolean} [scheduledNotification] If set, it will add the scheduled notification warning about delays between the expected live live time and the actual time.
         */
        notifyPurge(modelName, operationName, purgeTime, scheduledNotification) {
            if (purgeTime !== undefined) {
                NotificationBar.open(this.createPurgeMessage(modelName, operationName, purgeTime, scheduledNotification), {closeDelay: DEFAULT_CLOSE_DELAY});
            }
        },

        /**
         * Notifies the user via Notification Bar.
         * @param {String} message The message to present in the Notification Bar.
         * @param {Number} closeDelay The time it takes for the notification to close.
         */
        notify(message, closeDelay = DEFAULT_CLOSE_DELAY) {
            NotificationBar.open(message, {closeDelay});
        },

        /**
         * Messages associated with managing fonts.
         * @type {Object}
         * @see  EditorWebFontController
         */
        ManageFonts: {
            noCommunication(integrationName, keyName) {
                if (Permissions.Roles.MANAGER.can()) {
                    const message =
                        `Unable to communicate with the ${integrationName} API. Make sure to  enter a valid ${keyName} ` +
                        "on the <a href='#fonts'>fonts setup page</a>.";

                    NotificationBar.error(message);
                }
            },

            invalidKey(integrationName, keyName, key, validateEvent) {
                if (Permissions.Roles.MANAGER.can()) {
                    const message =
                        `The ${integrationName} ${keyName} &quot;${escape(key)}&quot; appears to be invalid.`;

                    NotificationBar.error(message, {closeDelay: DEFAULT_CLOSE_DELAY});

                    instance.trigger(validateEvent, false);
                }
            },

            removedKey(integrationName, keyName) {
                if (Permissions.Roles.MANAGER.can()) {
                    const message =
                        `The ${keyName} has been removed. This will not impact live expriences, but ` +
                        `you will no longer be able to use ${integrationName} fonts in new experiences. Editing ` +
                        `an experience will remove any fonts from ${integrationName} from the experience.`;

                    NotificationBar.open(message, {closeDelay: DEFAULT_CLOSE_DELAY});
                }
            },

            validKey(integrationName, keyName, validateEvent) {
                if (Permissions.Roles.MANAGER.can()) {
                    const message =
                        `You have entered a valid ${integrationName} ${keyName}. You can now start using ${integrationName} fonts.`;

                    NotificationBar.open(message, {closeDelay: DEFAULT_CLOSE_DELAY});

                    instance.trigger(validateEvent, true);
                }
            }
        }
    });

    // Singleton
    instance = new SystemMessages();

    return instance;
});



// WEBPACK FOOTER //
// ./src/editor/services/SystemMessages.js